import React from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  setPrimaryWheel,
  clearPrimaryWheel,
} from "../../../redux/reducers/gallery";
import { useGetPrimaryWheelV2 } from "../../../services/queries";
import WheelComponent from "../../WheelComponent";
import TextView from "../../TextView";
import * as Styled from "./style";
import { ASSETS } from "../../../utils/config";
import {
  transformEditorTextNoStyles,
  getLoggedInUser,
  isUserLoggedIn,
} from "../../../utils/helpers";
import ContentEditable from "react-contenteditable";
import WheelCardThreeDotMenu, {
  menuItemKeys,
} from "../../WheelCardThreeDotMenu/WheelCardThreeDotMenu";
import PrimaryWheelPlaceHolder from "./PrimaryWheelPlaceHolder";
import { getWheelBackground } from "./primaryWheelBackground";

const DescriptionContainer = styled.div`
  font-size: 12px;
  line-height: 16px;
  cursor: pointer;
  font-weight: 400;
  font-family: Poppins;
  padding-bottom: 40px;
  word-break: break-word;

  & h1 {
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    margin: 0;
  }

  & p {
    margin: 0 !important;
    margin-bottom: 0 !important;
  }

  & p:not(:last-child) {
    margin-bottom: 8px !important;
  }

  & br:first-child {
    display: none;
  }
`;

const PrimaryWheel = ({
  userId,
  primaryWheelData,
  isLoading: isLoadingProp,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use passed props if available, otherwise fetch from hook
  const {
    data: hookPrimaryWheel,
    isLoading: hookIsLoading,
    isError,
    error,
  } = useGetPrimaryWheelV2(primaryWheelData ? null : userId);

  // Use props if available, otherwise use hook data
  const primaryWheel = primaryWheelData || hookPrimaryWheel;
  const isLoading = isLoadingProp !== undefined ? isLoadingProp : hookIsLoading;

  React.useEffect(() => {
    if (isError) {
      toast.error("Failed to load the primary wheel.");
    }
  }, [isError, error]);

  React.useEffect(() => {
    if (primaryWheel?.id) {
      dispatch(setPrimaryWheel(primaryWheel));
    }
  }, [primaryWheel]);

  const handleRedirection = () => {
    navigate(`/wheel/${primaryWheel?.id}`);
  };

  const getBackground = (bg) => {
    const isImageUrl = (bg) => /^(http|https|data):/.test(bg);
    return isImageUrl(bg)
      ? `url(${bg}) center/cover no-repeat`
      : bg || "#F6F7F9";
  };

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", height: "100%", flex: 1.4 }}>
        <Skeleton
          style={{ borderRadius: "8px" }}
          variant="rectangular"
          width="100%"
          height="100%"
        />
      </Box>
    );
  }

  if (!primaryWheel || Object.keys(primaryWheel).length === 0) {
    return <PrimaryWheelPlaceHolder />;
  }

  const getMenuItems = () => {
    const isLoggedIn = isUserLoggedIn();
    if (!isLoggedIn) {
      return [
        menuItemKeys.CLONE,
        primaryWheel?.is_locked ? menuItemKeys.UNLOCK : null,
        menuItemKeys.SHARE,
        menuItemKeys.FAVORITE,
        menuItemKeys.REPORT,
      ];
    }

    console.log(primaryWheel.description,"DESC")

    const isOwnProfile = parseInt(userId) === parseInt(getLoggedInUser()?.id);

    if (!isOwnProfile) {
      return [
        menuItemKeys.CLONE,
        primaryWheel?.is_locked ? menuItemKeys.UNLOCK : null,
        menuItemKeys.SHARE,
        menuItemKeys.FAVORITE,
        menuItemKeys.REPORT,
      ];
    }

    return [
      menuItemKeys.EDIT,
      menuItemKeys.VISIBILITY,
      menuItemKeys.SHARE,
      menuItemKeys.DELETE,
      menuItemKeys.REMOVE_PRIMARY,
    ];
  };

  // Helper function to clean HTML content
  const cleanDescriptionHtml = (html) => {
    if (!html) return "";

    // Remove leading whitespace and line breaks
    let cleaned = html.replace(/^\s+/, "");

    // Remove leading empty tags with whitespace
    cleaned = cleaned.replace(/^(<[^>]*>)*\s+/, "$1");

    // Remove leading <br> tags
    cleaned = cleaned.replace(/^(<br\s*\/?>)+/i, "");

    // Remove leading <p> tags that only contain whitespace
    cleaned = cleaned.replace(/^<p[^>]*>\s*<\/p>/i, "");

    return cleaned;
  };

  return (
    <Box
      className="primary-wheel"
      sx={{ height: "100%", width: "100%", margin: 0, padding: 0 }}
    >
      <Styled.CardContainer
        background={
          primaryWheel?.version === 1
            ? getBackground(primaryWheel?.wallpaper)
            : getBackground(
                getWheelBackground(primaryWheel?.wallpaper_settings)
              )
        }
        style={{ height: "100%" }}
      >
        <Styled.WrapperGrid>
          <Styled.WheelView onClick={handleRedirection}>
            <WheelComponent
              userData={{
                id: primaryWheel?.id,
                url: primaryWheel?.wheel_image || ASSETS.IyugaWhiteLogo,
              }}
              maxSelectedArcs={0}
              arcThickness={-10}
              chartData={primaryWheel?.wheel_segments || []}
              margin={1}
              setChartData={() => {}}
              containerDivStyle={{
                width: "12vw",
                height: "12vw",
              }}
            />
          </Styled.WheelView>
          <Styled.ContentView>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                width: "100%",
                flexShrink: 0,
                minHeight: "24px",
              }}
            >
              <TextView
                onClick={handleRedirection}
                variant="title"
                style={{
                  cursor: "pointer",
                  WebkitLineClamp: 2,
                  color: "#14606F",
                  maxWidth: "80%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: "20px",
                  fontSize: "22px",
                }}
              >
                <ContentEditable
                  html={transformEditorTextNoStyles(primaryWheel?.wheel_name)}
                  disabled
                />
              </TextView>
              <WheelCardThreeDotMenu
                data={primaryWheel}
                include={getMenuItems()}
                refetchPrimaryOnFavourite={true}
              />
            </div>
            <Box
              sx={{
                position: "relative",
                height: "calc(100% - 32px)",
                marginTop: 0.5,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                // "&::after": {
                //   content: '""',
                //   position: "absolute",
                //   bottom: 0,
                //   left: 0,
                //   right: 0,
                //   height: "40px",
                //   background:
                //     "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 49%, #FFFFFF 99%)",
                //     backdropFilter: "blur(4px)",
                //   pointerEvents: "none",
                // },
              }}
              onClick={handleRedirection}
            >
              <Box
                sx={{
                  overflowY: "auto",
                  height: "100%",
                  paddingRight: "8px",
                  overflowX: "hidden",
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#9B5AF8",
                    borderRadius: "4px",
                    "&:hover": {
                      background: "#81459B",
                    },
                  },
                }}
              >
                <DescriptionContainer>
                  <ContentEditable
                    html={cleanDescriptionHtml(primaryWheel?.description)}
                    disabled
                  />
                </DescriptionContainer>
              </Box>
            </Box>
          </Styled.ContentView>
        </Styled.WrapperGrid>
      </Styled.CardContainer>
    </Box>
  );
};

export default PrimaryWheel;
